<div class="ra-page">
    <p-toolbar styleClass="ra-toolbar">
        <ng-template pTemplate="left">
            <h1 class="ra-title">Recommendations Dashboard</h1>
        </ng-template>
        <ng-template pTemplate="right">
            <button pButton type="button" label="Go to Orders" icon="pi pi-list" (click)="goToOrders()"></button>
        </ng-template>
    </p-toolbar>

    <div class="ra-filters">
        <p-selectButton
            [options]="dateViewOptions"
            [(ngModel)]="selectedView"
            (onChange)="onDateViewChange()"
            optionLabel="label"
            optionValue="value"
        ></p-selectButton>

        <ng-container *ngIf="selectedView === 'custom'">
            <label>
                From
                <input type="date" class="p-inputtext p-component" [(ngModel)]="fromDate" (change)="onCustomDateChange()" [max]="toDate" />
            </label>
            <label>
                To
                <input type="date" class="p-inputtext p-component" [(ngModel)]="toDate" (change)="onCustomDateChange()" [min]="fromDate" />
            </label>
        </ng-container>

        <button pButton type="button" label="Refresh" icon="pi pi-refresh" class="p-button-outlined" (click)="reload()" [disabled]="loading"></button>
    </div>

    <p class="ra-hint" *ngIf="rangeHint">{{ rangeHint }}</p>
    <div class="ra-error" *ngIf="error">{{ error }}</div>

    <div class="ra-loading" *ngIf="loading">
        <p-progressSpinner strokeWidth="4"></p-progressSpinner>
    </div>

    <ng-container *ngIf="!loading && summary as s">
        <section class="ra-banner" aria-label="Recommendation audit summary">
            <div class="ra-banner-group">
                <article class="ra-metric">
                    <span class="ra-metric-label">Audited lines</span>
                    <span class="ra-metric-value">{{ s.totalAuditedLines | number }}</span>
                    <span class="ra-metric-hint">V2 lines with add source</span>
                </article>
                <article class="ra-metric">
                    <span class="ra-metric-label">Orders</span>
                    <span class="ra-metric-value">{{ s.distinctOrderCount | number }}</span>
                    <span class="ra-metric-hint">with audited lines</span>
                </article>
            </div>

            <div class="ra-banner-group ra-banner-group--accent">
                <article class="ra-metric">
                    <span class="ra-metric-label">Select share</span>
                    <span class="ra-metric-value">{{ s.selectSharePercent | number: '1.0-1' }}%</span>
                    <span class="ra-metric-hint">clicked recommendation</span>
                </article>
                <article class="ra-metric">
                    <span class="ra-metric-label">Overlap rate</span>
                    <span class="ra-metric-value">{{ s.overlapRatePercent | number: '1.0-1' }}%</span>
                    <span class="ra-metric-hint">yes ÷ (yes + no)</span>
                </article>
                <article class="ra-metric">
                    <span class="ra-metric-label">Select</span>
                    <span class="ra-metric-value">{{ s.recommendationSelectCount | number }}</span>
                    <span class="ra-metric-hint">from recommendation panel</span>
                </article>
                <article class="ra-metric">
                    <span class="ra-metric-label">No reco</span>
                    <span class="ra-metric-value">{{ (s.wasRecommendedNoReco ?? 0) | number }}</span>
                    <span class="ra-metric-hint">no list for customer</span>
                </article>
            </div>

            <div class="ra-banner-group ra-banner-group--sources">
                <article class="ra-metric">
                    <span class="ra-metric-label">Manual</span>
                    <span class="ra-metric-value">{{ s.manualCount | number }}</span>
                    <span class="ra-metric-hint">typed add</span>
                </article>
                <article class="ra-metric">
                    <span class="ra-metric-label">Copy</span>
                    <span class="ra-metric-value">{{ s.copyCount | number }}</span>
                    <span class="ra-metric-hint">from another order</span>
                </article>
                <article class="ra-metric">
                    <span class="ra-metric-label">Excel</span>
                    <span class="ra-metric-value">{{ s.excelCount | number }}</span>
                    <span class="ra-metric-hint">import upload</span>
                </article>
            </div>
        </section>

        <div class="ra-grid">
            <p-card header="Add source mix" styleClass="ra-section">
                <p-table [value]="sourceMix" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '100%' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Source</th>
                            <th>Lines</th>
                            <th>Share</th>
                            <th style="width: 40%"></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>{{ row.label }}</td>
                            <td>{{ row.count | number }}</td>
                            <td>{{ row.pct | number: '1.0-1' }}%</td>
                            <td><p-progressBar [value]="row.pct" [showValue]="false"></p-progressBar></td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="4">No audited recommendation lines in this range.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-card>

            <p-card header="Top salespersons (by audited lines)" styleClass="ra-section">
                <p-table [value]="bySalesperson" styleClass="p-datatable-sm" [tableStyle]="{ 'min-width': '100%' }">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Salesperson</th>
                            <th>Lines</th>
                            <th>Select</th>
                            <th>Overlap %</th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-row>
                        <tr>
                            <td>{{ row.userIdResponsible }}</td>
                            <td>{{ row.totalAuditedLines | number }}</td>
                            <td>{{ row.recommendationSelectCount | number }}</td>
                            <td>{{ overlapPct(row.wasRecommendedTrue, row.wasRecommendedFalse) | number: '1.0-1' }}%</td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr>
                            <td colspan="4">No salesperson data in this range.</td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-card>
        </div>

        <p-card header="Daily trend (aggregated)" styleClass="ra-section ra-daily">
            <p class="ra-section-note">Counts only — not individual order lines.</p>
            <p-table
                [value]="daily"
                styleClass="p-datatable-sm"
                [scrollable]="true"
                scrollHeight="420px"
                [tableStyle]="{ 'min-width': '48rem' }"
            >
                <ng-template pTemplate="header">
                    <tr>
                        <th>Date</th>
                        <th>Lines</th>
                        <th>Select</th>
                        <th>Manual</th>
                        <th>Copy</th>
                        <th>Excel</th>
                        <th>Overlap yes</th>
                        <th>Overlap no</th>
                        <th>No reco</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>{{ row.requestedDate }}</td>
                        <td>{{ row.totalAuditedLines | number }}</td>
                        <td>{{ row.recommendationSelectCount | number }}</td>
                        <td>{{ row.manualCount | number }}</td>
                        <td>{{ row.copyCount | number }}</td>
                        <td>{{ row.excelCount | number }}</td>
                        <td>{{ row.wasRecommendedTrue | number }}</td>
                        <td>{{ row.wasRecommendedFalse | number }}</td>
                        <td>{{ row.wasRecommendedNoReco | number }}</td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="9">No daily aggregates in this range.</td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>
    </ng-container>
</div>
